import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router";

import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { GLOBAL_SHORTCUTS, SHORTCUT_SECTIONS } from "@/lib/keyboard-shortcuts";
import { cn, isUserTyping } from "@/lib/utils";
import { ShortcutEntry, ShortcutSection, SHORTCUT_SECTION_ID } from "@/types";

// -- Sub-components ----------------------------------------------------------

const ShortcutRow = ({
  entry,
  isLast,
}: {
  entry: ShortcutEntry;
  isLast: boolean;
}) => (
  <Row
    className={cn(
      "justify-between px-3 py-2",
      !isLast && "border-b border-slate-100",
    )}
  >
    <span className="text-base text-slate-600">{entry.description}</span>
    <span className="h-fit shrink-0 rounded border border-slate-200 bg-slate-50 px-2 py-0.5 text-base text-slate-700">
      {entry.keys}
    </span>
  </Row>
);

const Section = ({
  section,
}: {
  section: { label: string; shortcuts: ShortcutEntry[] };
}) => (
  <Column className="overflow-hidden rounded-lg border border-slate-200">
    <div className="border-b border-slate-200 bg-slate-100 px-3 py-2">
      <span className="text-sm font-semibold text-slate-700">
        {section.label}
      </span>
    </div>
    {section.shortcuts.map((entry, i) => (
      <ShortcutRow
        key={entry.description}
        entry={entry}
        isLast={i === section.shortcuts.length - 1}
      />
    ))}
  </Column>
);

// -- Context detection -------------------------------------------------------

function useContextSectionIds(): SHORTCUT_SECTION_ID[] {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get("t");

  const ids: SHORTCUT_SECTION_ID[] = [];

  if (searchParams.has("search")) {
    ids.push(SHORTCUT_SECTION_ID.SEARCH_RESULT_SHEET);
  } else if (searchParams.has("c")) {
    ids.push(SHORTCUT_SECTION_ID.CANDIDATE_SHEET);
  }

  if (pathname.startsWith("/tools/job-tracker")) {
    ids.push(SHORTCUT_SECTION_ID.JOB_UPDATE_MODAL);
  }

  if (tab === "intake" || tab === "job-intake") {
    ids.push(SHORTCUT_SECTION_ID.COMPANY_JOB_INTAKE);
  }

  if (tab === "reqs") {
    ids.push(SHORTCUT_SECTION_ID.REQUISITIONS);
  }

  if (/^\/companies\/[^/]+\/jobs\/[^/]+/.test(pathname)) {
    ids.push(SHORTCUT_SECTION_ID.JOB_PAGE);
  }

  return ids;
}

// -- Modal -------------------------------------------------------------------

const globalSection: ShortcutSection = {
  id: SHORTCUT_SECTION_ID.GLOBAL,
  label: "Global",
  shortcuts: GLOBAL_SHORTCUTS,
};

export const KeyboardShortcutsModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const contextSectionIds = useContextSectionIds();

  const contextSections = SHORTCUT_SECTIONS.filter((s) =>
    contextSectionIds.includes(s.id),
  );
  const otherSections = SHORTCUT_SECTIONS.filter(
    (s) => !contextSectionIds.includes(s.id),
  );

  // Reset "show all" when the modal closes so it reopens in the focused state.
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) setShowAll(false);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "?" && !isUserTyping()) {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[80vh] max-w-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Keyboard Shortcuts</DialogTitle>
        </DialogHeader>
        <Column className="gap-4">
          <Section section={globalSection} />
          {contextSections.map((section) => (
            <Section key={section.id} section={section} />
          ))}
          {otherSections.length > 0 && (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="h-auto justify-start p-0 text-xs text-slate-400 hover:bg-transparent hover:text-slate-600"
                onClick={() => {
                  setShowAll((prev) => !prev);
                }}
              >
                {showAll ? "Show fewer" : "Show all shortcuts"}
              </Button>
              {showAll &&
                otherSections.map((section) => (
                  <Section key={section.id} section={section} />
                ))}
            </>
          )}
        </Column>
      </DialogContent>
    </Dialog>
  );
};
